import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import BookingModal from './BookingModal';
import AuthModals from './AuthModals';
import '../styles/LegalPages.css';

const TermsConditions: React.FC = () => {
  const [showLogin, setShowLogin] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [showBooking, setShowBooking] = useState(false);

  return (
    <div className="legal-page">
      <Header 
        onOpenLogin={() => setShowLogin(true)}
        onOpenSignup={() => setShowSignup(true)}
      />
      
      {/* Hero Banner */}
      <section className="legal-banner">
        <div className="banner-overlay"></div>
        <div className="banner-content">
          <h1 className="banner-title">Terms & Conditions</h1>
          <p className="banner-subtitle">
            Clear and transparent terms governing our legal consultation services and platform usage.
          </p>
          <div className="banner-icons">
            <div className="banner-icon">
              <i className="fas fa-gavel"></i>
              <span>Legal</span>
            </div>
            <div className="banner-icon">
              <i className="fas fa-handshake"></i>
              <span>Fair</span>
            </div>
            <div className="banner-icon">
              <i className="fas fa-balance-scale"></i>
              <span>Balanced</span>
            </div>
          </div>
        </div>
      </section>

      <div className="legal-page-container">        

        <div className="legal-content">
          <section>
            <h2>1. Acceptance of Terms</h2>
            <p>By accessing and using WeLegal's services, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.</p>
          </section>

          <section>
            <h2>2. Service Description</h2>
            <p>WeLegal is a platform that connects clients with qualified legal professionals for consultations and legal services. We provide:</p>
            <ul>
              <li>Virtual and doorstep legal consultations</li>
              <li>Clear and transparent pricing structure</li>
              <li>Access to verified lawyers across Delhi NCR</li>
              <li>Secure booking and payment processing</li>
              <li>Legal advice across multiple practice areas</li>
            </ul>
          </section>

          <section>
            <h2>3. User Responsibilities</h2>
            <h3>Account Registration</h3>
            <ul>
              <li>Provide accurate and complete information during registration</li>
              <li>Maintain the confidentiality of your account credentials</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
              <li>You are responsible for all activities under your account</li>
            </ul>

            <h3>Consultation Conduct</h3>
            <ul>
              <li>Attend scheduled consultations on time</li>
              <li>Provide truthful and complete information to lawyers</li>
              <li>Respect lawyer's time and professional boundaries</li>
              <li>Follow cancellation policies for rescheduling</li>
            </ul>
          </section>

          <section>
            <h2>4. Booking and Payment Terms</h2>
            <h3>Booking Policy</h3>
            <ul>
              <li>Consultations must be booked at least 2 hours in advance</li>
              <li>60-minute consultation sessions available</li>
              <li>Payment required at the time of booking</li>
              <li>Confirmation will be sent via email and SMS</li>
            </ul>

            <h3>Cancellation Policy</h3>
            <ul>
              <li>Free cancellation up to 4 hours before consultation</li>
              <li>50% refund for cancellations 2-4 hours before consultation</li>
              <li>No refund for cancellations less than 2 hours before consultation</li>
              <li>Emergency cancellations will be reviewed case-by-case</li>
            </ul>

            <h3>Refund Policy</h3>
            <ul>
              <li>Refunds processed within 5-7 business days</li>
              <li>Refunds issued to the original payment method</li>
              <li>Service fees may apply for processing refunds</li>
            </ul>
          </section>

          <section>
            <h2>5. Lawyer-Client Relationship</h2>
            <p><strong>Important:</strong> WeLegal facilitates connections between clients and lawyers but does not provide legal advice directly.</p>
            <ul>
              <li>Attorney-client privilege applies to your consultations</li>
              <li>Lawyers are independent professionals, not WeLegal employees</li>
              <li>WeLegal is not responsible for the outcome of legal matters</li>
              <li>Clients should verify lawyer credentials and specializations</li>
              <li>Additional legal services beyond consultation require separate agreements</li>
            </ul>
          </section>

          <section>
            <h2>6. Platform Usage Rules</h2>
            <p>Users agree not to:</p>
            <ul>
              <li>Use the platform for illegal or unauthorized purposes</li>
              <li>Interfere with or disrupt the service or servers</li>
              <li>Attempt to gain unauthorized access to user accounts</li>
              <li>Upload malicious software or harmful content</li>
              <li>Impersonate other users or provide false information</li>
              <li>Spam or harass other users or lawyers</li>
            </ul>
          </section>

          <section>
            <h2>7. Intellectual Property</h2>
            <p>All content on the WeLegal platform, including text, graphics, logos, and software, is the property of WeLegal and is protected by copyright and other intellectual property laws.</p>
            <ul>
              <li>Users may not reproduce, distribute, or create derivative works</li>
              <li>Limited license granted for personal, non-commercial use</li>
              <li>Trademarks and service marks remain property of WeLegal</li>
            </ul>
          </section>

          <section>
            <h2>8. Limitation of Liability</h2>
            <p>WeLegal's liability is limited to the maximum extent permitted by law:</p>
            <ul>
              <li>We do not guarantee specific legal outcomes</li>
              <li>Our liability is limited to the consultation fees paid</li>
              <li>We are not responsible for lawyer performance or advice quality</li>
              <li>Force majeure events are excluded from liability</li>
            </ul>
          </section>

          <section>
            <h2>9. Disclaimers</h2>
            <ul>
              <li><strong>No Legal Advice:</strong> WeLegal does not provide legal advice; we connect you with lawyers who do</li>
              <li><strong>No Guarantee:</strong> We do not guarantee specific outcomes or results</li>
              <li><strong>Service Availability:</strong> Services may be temporarily unavailable due to maintenance</li>
              <li><strong>Third-Party Content:</strong> We are not responsible for third-party content or links</li>
            </ul>
          </section>

          <section>
            <h2>10. Privacy and Confidentiality</h2>
            <p>We are committed to protecting your privacy and maintaining confidentiality:</p>
            <ul>
              <li>Consultation details are kept strictly confidential</li>
              <li>Information is shared only with assigned lawyers</li>
              <li>Data is protected according to our Privacy Policy</li>
              <li>Attorney-client privilege is respected and maintained</li>
            </ul>
          </section>

          <section>
            <h2>11. Dispute Resolution</h2>
            <p>Any disputes arising from these terms shall be resolved through:</p>
            <ul>
              <li>Initial good faith negotiation between parties</li>
              <li>Mediation through recognized mediation services</li>
              <li>Arbitration under Indian Arbitration and Conciliation Act, 2015</li>
              <li>Courts in Delhi shall have exclusive jurisdiction</li>
            </ul>
          </section>

          <section>
            <h2>12. Modifications</h2>
            <p>WeLegal reserves the right to modify these terms at any time. Material changes will be communicated through:</p>
            <ul>
              <li>Email notifications to registered users</li>
              <li>Platform notifications upon login</li>
              <li>Updated terms posted on our website</li>
            </ul>
            <p>Continued use of the service after modifications constitutes acceptance of new terms.</p>
          </section>

          <section>
            <h2>13. Governing Law</h2>
            <p>These terms are governed by the laws of India. Any legal action or proceeding shall be brought exclusively in the courts of Delhi, India.</p>
          </section>

          <section>
            <h2>14. Contact Information</h2>
            <p>For questions about these Terms and Conditions, please contact us:</p>
            <div className="contact-info">
              <p><strong>Email:</strong> legal@welegal.co</p>
              <p><strong>Phone:</strong> +91 9999999999</p>
              <p><strong>Address:</strong> WeLegal Legal Services, Delhi NCR, India</p>
            </div>
          </section>

          <section>
            <h2>15. Severability</h2>
            <p>If any provision of these terms is found to be unenforceable, the remaining provisions will continue to be valid and enforceable.</p>
          </section>
        </div>
      </div>

      <Footer />
      
      <BookingModal 
        isOpen={showBooking}
        onClose={() => setShowBooking(false)}
      />
      <AuthModals 
        showLogin={showLogin}
        showSignup={showSignup}
        onCloseLogin={() => setShowLogin(false)}
        onCloseSignup={() => setShowSignup(false)}
        onOpenSignup={() => setShowSignup(true)}
        onOpenLogin={() => setShowLogin(true)}
      />
    </div>
  );
};

export default TermsConditions;